/** 1.3.0 portal: My deliveries (each assignment a checklist of its items) and one delivery's page. */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { IntakeNoteDTO } from '@prism/contracts';
import { t } from '../i18n/en';
import { errorText, portalApi } from '../client';
import { formatBytes, formatDate } from '../format';
import { Chip, FieldsForm, ItemUploader, Loading, RightsBox, Thread, missingField, useLoad, useSession } from '../components';
import { pendingFor, uploadFor } from '../uploader';
import type { PortalDelivery, PortalItem } from '../types';
import { GroupedDeliveries } from '../groups/GroupedDeliveries';

const takesUploads = (d: PortalDelivery) => ['open', 'in_progress', 'in_review'].includes(d.state) || (d.state === 'accepted' && d.items.some((i) => i.state === 'open' || i.state === 'rejected'));
const itemKey = (i: PortalItem) => `item:${i.id}`;
const busy = (d: PortalDelivery) => d.items.some((i) => i.processing === 'registered' || i.processing === 'processing') || d.submission?.qcStatus === 'queued' || d.submission?.qcStatus === 'running';

/** 1.3.1 (Q2): an expired delivery's notice (it takes no more files). */
function ExpiredNotice({ d }: { d: PortalDelivery }) {
  if (d.state !== 'expired') return null;
  const at = (d as PortalDelivery & { expiredAt?: string | null }).expiredAt;
  return <p className="p-notice p-notice-warn" role="status" data-testid="delivery-expired">{t('reminders.expired', { date: at ? formatDate(at) : '' })}</p>;
}

function due(d: PortalDelivery) {
  if (!d.dueAt) return <span className="p-muted">{t('portal.deliveries.noDue')}</span>;
  const late = new Date(d.dueAt).getTime() < Date.now() && ['open', 'in_progress', 'in_review'].includes(d.state);
  return <span className={late ? 'p-bad' : 'p-muted'}>{t(late ? 'portal.deliveries.overdue' : 'portal.deliveries.due', { date: formatDate(d.dueAt) })}</span>;
}

/** What a file must be, in plain words. */
function rulesText(d: PortalDelivery, item: PortalItem): string {
  const parts: string[] = [];
  const types = item.kind === 'media' ? d.mediaRules.acceptedTypes : item.acceptedTypes;
  parts.push(types.length ? t('portal.delivery.accepts', { types: types.map((x) => x.toUpperCase()).join(', ') }) : item.kind === 'media' ? t('portal.delivery.anyMedia') : t('portal.delivery.anyFile'));
  if (item.kind === 'media') {
    parts.push(t('portal.delivery.maxSize', { size: formatBytes(d.mediaRules.maxBytes) }));
    if (d.mediaRules.namingRule) parts.push(t('portal.delivery.naming', { rule: d.mediaRules.namingRule }));
  }
  return parts.join(' · ');
}

/** One requested item: state, description, its file, and the upload. */
export function ItemRow({ d, item, onChanged, compact }: { d: PortalDelivery; item: PortalItem; onChanged: () => void; compact?: boolean }) {
  const key = itemKey(item);
  const running = Boolean(uploadFor(key)?.active || pendingFor(key));
  const [open, setOpen] = useState(false);
  const canUpload = takesUploads(d) && ['open', 'uploaded', 'rejected'].includes(item.state);
  const showUploader = canUpload && (open || running || (!compact && item.state !== 'uploaded'));
  return (
    <li className="p-item" data-testid={`item-${item.kind}`} data-state={item.state}>
      <div className="p-item-head">
        <span className={`p-tick p-tick-${item.state}`} aria-hidden="true" />
        <div className="p-item-main">
          <div className="p-item-title">
            <strong>{item.label}</strong>
            <span className="p-muted">{item.required ? t('portal.delivery.required') : t('portal.delivery.optional')}</span>
          </div>
          {item.description ? <p className="p-item-desc">{item.description}</p> : null}
          <p className="p-small p-muted">{rulesText(d, item)}</p>
          {item.file ? (
            <p className="p-small">
              <span className="p-file">{item.file.filename}</span> · {formatBytes(item.file.sizeBytes)}
              {item.kind === 'media' && item.processing ? <> · <span className="p-muted">{t(`portal.state.processing.${item.processing}`)}</span></> : null}
            </p>
          ) : null}
          {item.state === 'rejected' && item.reason ? <p className="p-bad p-small">{t('portal.item.rejectedReason', { reason: item.reason })}</p> : null}
        </div>
        <Chip kind="item" state={item.state} />
        {canUpload && !showUploader ? (
          <button type="button" className="p-btn p-btn-small" onClick={() => setOpen(true)} data-testid="item-upload-button">
            {item.state === 'open' ? t('portal.item.upload') : item.state === 'rejected' ? t('portal.item.uploadAgain') : t('portal.item.replace')}
          </button>
        ) : null}
      </div>
      {showUploader ? (
        <ItemUploader
          uploadKey={key} target={{ itemId: item.id }} media={item.kind === 'media'}
          acceptedTypes={item.kind === 'media' ? d.mediaRules.acceptedTypes : item.acceptedTypes}
          onDone={() => { setOpen(false); onChanged(); }}
        />
      ) : null}
    </li>
  );
}

function DeliveryCard({ d, onChanged }: { d: PortalDelivery; onChanged: () => void }) {
  const done = d.items.filter((i) => i.file || i.state === 'accepted' || i.state === 'waived').length;
  return (
    <article className="p-card" data-testid="delivery-card">
      <header className="p-card-head">
        <div>
          <h2><Link to={`/deliveries/${d.id}`}>{d.asset.title}</Link></h2>
          <p className="p-small p-muted"><span className="p-code">{d.asset.assetCode}</span> · {due(d)} · {t('portal.deliveries.itemsDone', { done, total: d.items.length })}</p>
        </div>
        <Chip kind="assignment" state={d.state} />
      </header>
      <ExpiredNotice d={d} />
      <ul className="p-items">{d.items.map((i) => <ItemRow key={i.id} d={d} item={i} onChanged={onChanged} compact />)}</ul>
      <div className="p-row p-end"><Link className="p-btn" to={`/deliveries/${d.id}`}>{t('portal.deliveries.open')}</Link></div>
    </article>
  );
}

export function DeliveriesPage({ welcome }: { welcome: string }) {
  const { data, error, reload } = useLoad<{ items: PortalDelivery[] }>('/deliveries', { poll: (v) => v.items.some(busy), pollMs: 5000 });
  return (
    <>
      <div className="p-page-head">
        <h1>{t('portal.myDeliveries')}</h1>
        {welcome ? <p className="p-pre p-muted">{welcome}</p> : null}
      </div>
      {!data ? <Loading error={error} retry={reload} /> : data.items.length === 0 ? <p className="p-empty">{t('portal.deliveries.empty')}</p> : (
        // 1.4.0 (G3): a group's deliveries together, "Series › Season", in episode order.
        <GroupedDeliveries items={data.items} render={(d) => <DeliveryCard key={d.id} d={d} onChanged={reload} />} />
      )}
    </>
  );
}

export function DeliveryPage() {
  const { id } = useParams();
  const session = useSession();
  const { data: d, error, reload } = useLoad<PortalDelivery>(`/deliveries/${id}`, { poll: busy, pollMs: 4000 });
  const sid = d?.submission?.id ?? null;
  const notes = useLoad<{ items: IntakeNoteDTO[] }>(sid ? `/submissions/${sid}/notes` : null, { poll: () => true, pollMs: 15000 });
  const [values, setValues] = useState<Record<string, string>>({});
  const [rights, setRights] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [sending, setSending] = useState(false);
  if (!d) return <Loading error={error} retry={reload} />;
  const submit = async () => {
    const miss = missingField(d.fields, values);
    if (miss) { setMsg({ ok: false, text: t('portal.fieldRequired', { field: miss.label }) }); return; }
    if (!rights) { setMsg({ ok: false, text: t('error.RIGHTS_REQUIRED') }); return; }
    setSending(true);
    try {
      await portalApi(`/deliveries/${d.id}/submit`, { method: 'POST', json: { metadata: values, rightsAccepted: true, rightsVersion: d.rights.version } });
      setMsg({ ok: true, text: t('portal.delivery.submitted') });
      reload();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setSending(false);
    }
  };
  const draft = d.state !== 'expired' && (!d.submission || d.submission.state === 'draft');
  return (
    <>
      <p><Link to="/" className="p-link">← {t('portal.myDeliveries')}</Link></p>
      <div className="p-page-head">
        <div className="p-row p-between">
          <h1>{d.asset.title}</h1>
          <Chip kind="assignment" state={d.state} />
        </div>
        <p className="p-muted"><span className="p-code">{d.asset.assetCode}</span> · {due(d)}</p>
        <ExpiredNotice d={d} />
      </div>
      {d.spec ? (
        <section className="p-card">
          <h3>{t('portal.delivery.spec')}: {d.spec.name}</h3>
          {d.spec.description ? <p className="p-pre">{d.spec.description}</p> : null}
        </section>
      ) : null}
      {d.notes ? (
        <section className="p-card">
          <h3>{t('portal.delivery.notes', { workspace: session?.workspaceName ?? 'PRISM' })}</h3>
          <p className="p-pre">{d.notes}</p>
        </section>
      ) : null}
      {!d.scanned ? <p className="p-notice p-notice-warn">{t('portal.upload.notScanned')}</p> : null}
      <section className="p-card">
        <h3>{t('portal.delivery.items')}</h3>
        <ul className="p-items">{d.items.map((i) => <ItemRow key={i.id} d={d} item={i} onChanged={() => { reload(); notes.reload(); }} />)}</ul>
      </section>
      {draft ? (
        <section className="p-card" data-testid="submit-card">
          <h3>{t('portal.submit')}</h3>
          <p className="p-muted">{t('portal.delivery.submitHint')}</p>
          {d.fields.length ? <><h4>{t('portal.delivery.fields')}</h4><FieldsForm fields={d.fields} values={values} onChange={setValues} /></> : null}
          <RightsBox text={d.rights.text} checked={rights} onChange={setRights} />
          <div className="p-row">
            <button type="button" className="p-btn p-btn-primary" disabled={!d.canSubmit || sending} onClick={() => void submit()} data-testid="submit">{t('portal.submit')}</button>
            {!d.canSubmit ? <span className="p-muted p-small">{t('portal.submit.needsMedia')}</span> : null}
          </div>
          {msg ? <p className={msg.ok ? 'p-good' : 'p-bad'} role="status">{msg.text}</p> : null}
        </section>
      ) : d.submission ? (
        <section className="p-card p-row p-between">
          <div className="p-row">
            <Chip kind="submission" state={d.submission!.state} />
            {d.submission!.qcStatus ? <Chip kind="qc" state={d.submission!.qcStatus} /> : null}
          </div>
          <Link className="p-btn p-btn-primary" to={`/submissions/${d.submission!.id}`} data-testid="see-result">{t('portal.delivery.result')}</Link>
        </section>
      ) : null}
      {draft && sid ? <p><Link className="p-link" to={`/submissions/${sid}`}>{t('portal.delivery.result')}</Link></p> : null}
      {sid ? (
        <section className="p-card">
          <h3>{t('portal.comments.title')}</h3>
          <Thread submissionId={sid} itemId={null} notes={notes.data?.items ?? []} onPosted={notes.reload} title={t('portal.comments.whole')} />
          {d.items.map((i) => (
            <Thread key={i.id} submissionId={sid} itemId={i.id} notes={notes.data?.items ?? []} onPosted={notes.reload} title={t('portal.comments.itemTitle', { item: i.label })} label={i.label} />
          ))}
        </section>
      ) : null}
    </>
  );
}
